import { useMemo } from 'react'
import * as THREE from 'three'

interface PropsLagrima {
  visivel?: boolean
  posicao?: [number, number, number]
  rotacao?: [number, number, number]
  escala?: number
  destaque?: boolean
}

/**
 * Cria um furo vazado de formato cápsula/estádio rotacionado para as pegas da Lágrima.
 */
function criarAlcaVazada(
  cx: number,
  cy: number,
  comprimento: number,
  largura: number,
  anguloRad: number,
): THREE.Path {
  const path = new THREE.Path()
  const r = largura / 2
  const d = Math.max(0, (comprimento - largura) / 2)
  const numSegArc = 8
  const cos = Math.cos(anguloRad)
  const sin = Math.sin(anguloRad)

  const tr = (u: number, v: number): [number, number] => [
    cx + u * cos - v * sin,
    cy + u * sin + v * cos,
  ]

  // Borda superior da cápsula
  const [x0, y0] = tr(-d, r)
  path.moveTo(x0, y0)
  const [x1, y1] = tr(d, r)
  path.lineTo(x1, y1)

  // Calota semicircular da extremidade direita
  for (let i = 1; i <= numSegArc; i++) {
    const a = Math.PI / 2 - (i / numSegArc) * Math.PI
    const [px, py] = tr(d + r * Math.cos(a), r * Math.sin(a))
    path.lineTo(px, py)
  }

  // Borda inferior da cápsula
  const [x2, y2] = tr(-d, -r)
  path.lineTo(x2, y2)

  // Calota semicircular da extremidade esquerda
  for (let i = 1; i <= numSegArc; i++) {
    const a = -Math.PI / 2 - (i / numSegArc) * Math.PI
    const [px, py] = tr(-d + r * Math.cos(a), r * Math.sin(a))
    path.lineTo(px, py)
  }

  path.closePath()
  return path
}

/**
 * Escudo Rígido de Proteção "Lágrima" (Teardrop Extrication Shield).
 *
 * Modelado fielmente com base na fotografia de referência do equipamento de salvamento veicular:
 * - Geometria anatômica em gota/lágrima assimétrica em polietileno de alta densidade (UHMW-PE).
 * - Duas pegas/alças vazadas anguladas ergonomicamente no bojo superior e lateral.
 * - Posicionado entre a vítima e as ferramentas hidráulicas/vidros quebrados para criar
 *   uma barreira física impenetrável contra estilhaços, fagulhas e cisalhamento metálico.
 */
export function LagrimaProtecao3D({
  visivel = true,
  posicao = [0, 0, 0],
  rotacao = [0, 0, 0],
  escala = 1,
  destaque = false,
}: PropsLagrima) {
  const { geometriaLagrima } = useMemo(() => {
    const shape = new THREE.Shape()

    // Contorno da lágrima baseado exatamente na silhueta da foto:
    // Bojo amplo no canto superior direito e cauda afilada/arredondada no canto inferior esquerdo.
    // Dimensões nominais: ~65 cm x 46 cm x 8 mm.
    const sx = 0.46
    const sy = 0.65

    // Início no domo superior do bojo
    shape.moveTo(0.24 * sx, 0.48 * sy)
    // Curva convexa do topo do bojo
    shape.bezierCurveTo(0.48 * sx, 0.46 * sy, 0.52 * sx, 0.28 * sy, 0.50 * sx, 0.08 * sy)
    // Curva convexa inferior direita do bojo
    shape.bezierCurveTo(0.46 * sx, -0.16 * sy, 0.32 * sx, -0.36 * sy, 0.08 * sx, -0.46 * sy)
    // Arco inferior contínuo até a ponta da cauda
    shape.bezierCurveTo(-0.16 * sx, -0.52 * sy, -0.38 * sx, -0.46 * sy, -0.48 * sx, -0.32 * sy)
    // Ponta arredondada da cauda curva
    shape.bezierCurveTo(-0.54 * sx, -0.20 * sy, -0.52 * sx, -0.06 * sy, -0.40 * sx, -0.02 * sy)
    // Cintura côncava característica conectando cauda ao bojo
    shape.bezierCurveTo(-0.24 * sx, 0.06 * sy, -0.12 * sx, 0.18 * sy, -0.06 * sx, 0.34 * sy)
    // Fechamento no topo do domo
    shape.bezierCurveTo(-0.02 * sx, 0.46 * sy, 0.10 * sx, 0.50 * sy, 0.24 * sx, 0.48 * sy)

    // Alça 1 (superior no bojo, angulada a ~42° acompanhando a curvatura do topo)
    const alcaSuperior = criarAlcaVazada(
      0.08 * sx,
      0.28 * sy,
      0.115,
      0.034,
      0.72,
    )
    shape.holes.push(alcaSuperior)

    // Alça 2 (lateral-inferior no bojo, angulada a ~-53° acompanhando a borda direita)
    const alcaLateral = criarAlcaVazada(
      0.34 * sx,
      -0.18 * sy,
      0.115,
      0.034,
      -0.92,
    )
    shape.holes.push(alcaLateral)

    const geom = new THREE.ExtrudeGeometry(shape, {
      depth: 0.008,
      bevelEnabled: true,
      bevelThickness: 0.0025,
      bevelSize: 0.0025,
      bevelSegments: 2,
    })

    geom.center()
    return { geometriaLagrima: geom }
  }, [])

  if (!visivel) return null

  // Sem `transmission` de propósito: com ela o three redesenha a cena opaca inteira
  // num buffer extra por quadro, e a lágrima aparece em toda demonstração da oficina.
  // Com `opacity` de 0,94 a refração não se vê.
  return (
    <group position={posicao} rotation={rotacao} scale={escala}>
      <mesh geometry={geometriaLagrima}>
        <meshPhysicalMaterial
          color={destaque ? '#ffffff' : '#f8fafc'}
          roughness={0.24}
          metalness={0.02}
          transparent
          opacity={0.94}
          side={THREE.DoubleSide}
          clearcoat={0.3}
          clearcoatRoughness={0.2}
        />
      </mesh>
    </group>
  )
}


